// src/components/RIForm/RIForm.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import {
  TextField,
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  FormHelperText,
  CircularProgress,
  Box,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalStyle from "./RIForm.module.css"; // keep existing styles
import UrlConstants from "../../utils/constants/urlConstants";
import HTTPService from "../../services/HTTPService";
import { getTokenLocal, handleUnwantedSpace } from "../../utils/common/common";
import { EFarmContext } from "../../Contexts/eFarmContext";
import { useNavigate } from "react-router-dom";

export default function RIForm({ editMode }) {
  const navigate = useNavigate();
  const { callToast, callLoader } = useContext(EFarmContext);

  // === LOCAL MODE STATE ===
  // - "add": blank form to add new RI
  // - "choose": user clicked “Edit RI” but hasn’t chosen which one
  // - "edit": a specific RI has been chosen and form is filled
  const [mode, setMode] = useState(editMode ?? "add");
  const [riList, setRiList] = useState([]);
  const [riLoading, setRiLoading] = useState(false);
  const [selectedRiId, setSelectedRiId] = useState("");

  // === FORM STATE (shared for both add/edit) ===
  const [riName, setRiName] = useState("");
  const [geography, setGeography] = useState({
    state: "",
    district: "",
    block: "",
    village: "",
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [villages, setVillages] = useState([]);

  // members can be mix of existing (with id) and new (no id)
  // existing shape: { id: "<uuid>", first_name, phone, gender, isNew: false }
  // new shape:      { id: null, first_name, phone, gender, isNew: true }
  const [members, setMembers] = useState([
    { id: null, first_name: "", phone: "", gender: "", isNew: true },
    // { id: null, first_name: "", phone: "", gender: "", isNew: true },
  ]);

  const [removedMemberIds, setRemovedMemberIds] = useState([]);

  const [errorMessage, setErrorMessage] = useState({
    riName: null,
    state: null,
    district: null,
    block: null,
    village: null,
    members: [], // one entry per member row, each: { first_name, phone, gender }
  });

  // Ref for scrolling to RI name error
  const riNameRef = useRef(null);

  // Snackbar for “Back” / “Clear”
  const [openSnackbar, setOpenSnackbar] = useState(null);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  // === VALIDATION & SCROLL HELPERS ===
  const scrollToError = (ref, offsetY = 20) => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      window.scrollTo({
        top: rect.top + window.scrollY - offsetY,
        behavior: "smooth",
      });
    }
  };

  // === FETCH GEO DATA ===
  const getStateList = () => {
    HTTPService(
      "GET",
      UrlConstants.base_url + UrlConstants.get_State,
      "",
      false,
      true,
      getTokenLocal()
    )
      .then((res) => setStates(res.data))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate("/login");
          callToast("Session expired, please login again!", "error", true);
        } else {
          callToast("Unable to fetch states", "error", true);
        }
      });
  };

  const getDistrictList = (stateId) => {
    HTTPService(
      "GET",
      UrlConstants.base_url +
        UrlConstants.get_District +
        `?state_id=${stateId}`,
      "",
      false,
      true,
      getTokenLocal()
    )
      .then((res) => setDistricts(res.data))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate("/login");
          callToast("Session expired, please login again!", "error", true);
        }
      });
  };

  const getBlockList = (districtId) => {
    HTTPService(
      "GET",
      UrlConstants.base_url +
        UrlConstants.get_Block +
        `?district_id=${districtId}`,
      "",
      false,
      true,
      getTokenLocal()
    )
      .then((res) => setBlocks(res.data))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate("/login");
          callToast("Session expired, please login again!", "error", true);
        }
      });
  };

  const getVillageList = (blockId) => {
    HTTPService(
      "GET",
      UrlConstants.base_url + UrlConstants.get_Village + `?block_id=${blockId}`,
      "",
      false,
      true,
      getTokenLocal()
    )
      .then((res) => setVillages(res.data))
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate("/login");
          callToast("Session expired, please login again!", "error", true);
        }
      });
  };

  // === HANDLERS FOR GEO SELECTS ===
  const handleStateChange = (e) => {
    const stateId = e.target.value;
    setGeography((prev) => ({
      ...prev,
      state: stateId,
      district: "",
      block: "",
      village: "",
    }));
    setDistricts([]);
    setBlocks([]);
    setVillages([]);
    getDistrictList(stateId);
  };

  const handleDistrictChange = (e) => {
    const districtId = e.target.value;
    setGeography((prev) => ({
      ...prev,
      district: districtId,
      block: "",
      village: "",
    }));
    setBlocks([]);
    setVillages([]);
    getBlockList(districtId);
  };

  const handleBlockChange = (e) => {
    const blockId = e.target.value;
    setGeography((prev) => ({
      ...prev,
      block: blockId,
      village: "",
    }));
    setVillages([]);
    getVillageList(blockId);
  };

  const handleVillageChange = (e) => {
    const villageId = e.target.value;
    setGeography((prev) => ({
      ...prev,
      village: villageId,
    }));
  };

  // === HANDLERS FOR MEMBERS ===
  const handleMemberChange = (index, field, value) => {
    const updated = [...members];
    // only alphabets+spaces allowed for first_name
    if (field === "first_name") {
      const alphabetsRegex = /^[a-zA-Z\s]*$/;
      if (!alphabetsRegex.test(value)) return;
    }
    // only digits for phone
    if (field === "phone") {
      const digitRegex = /^[0-9]*$/;
      if (!digitRegex.test(value) || value.length > 10) return;
    }
    // for gender, just set value
    updated[index][field] = value;
    setMembers(updated);
  };

  const addMemberRow = () => {
    setMembers((prev) => [
      ...prev,
      { id: null, first_name: "", phone: "", gender: "", isNew: true },
    ]);
    setErrorMessage((prev) => {
      const newMembersErr = [
        ...prev.members,
        { first_name: null, phone: null, gender: null },
      ];
      return { ...prev, members: newMembersErr };
    });
  };

  const removeMemberRow = (idx) => {
    const m = members[idx];
    if (!m.isNew && m.id) {
      // an existing member is being removed
      setRemovedMemberIds((prev) => [...prev, m.id]);
    }
    setMembers((prev) => prev.filter((_, i) => i !== idx));
    setErrorMessage((prev) => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== idx),
    }));
  };

  // === BACK / CLEAR SNACKBAR ===
  const handleBackClick = () => {
    setIsButtonClicked(false);
    setOpenSnackbar(true);
    // navigate(-1);
  };
  const handleClearClick = () => {
    setIsButtonClicked(true);
    setOpenSnackbar(true);
  };
  const handleWarningChoice = (choice) => {
    setOpenSnackbar(null);
    if (choice === "yes") {
      if (isButtonClicked) {
        clearForm();
        setMode("add");
      } else {
        navigate(-1);
      }
    }
  };
  const handleCloseSnackbar = () => {
    setOpenSnackbar(null);
  };
  const action = (
    <>
      <Button
        style={{
          color: "#212B36",
          border: "1px solid #212B36",
          borderRadius: "8px",
          height: "33px",
          width: "70px",
          textTransform: "none",
          fontFamily: "Montserrat",
          margin: "5px",
          fontWeight: "700",
          fontSize: "13px",
        }}
        size="small"
        onClick={() => handleWarningChoice("yes")}
      >
        Yes
      </Button>
      <Button
        style={{
          color: "#212B36",
          border: "1px solid #212B36",
          borderRadius: "8px",
          height: "33px",
          width: "70px",
          textTransform: "none",
          margin: "5px",
          fontFamily: "Montserrat",
          fontWeight: "700",
          fontSize: "13px",
        }}
        size="small"
        onClick={handleCloseSnackbar}
      >
        No
      </Button>
    </>
  );

  // === CLEAR FORM (resets everything) ===
  const clearForm = () => {
    setRiName("");
    setGeography({ state: "", district: "", block: "", village: "" });
    setDistricts([]);
    setBlocks([]);
    setVillages([]);
    setMembers([
      { id: null, first_name: "", phone: "", gender: "", isNew: true },
      // { id: null, first_name: "", phone: "", gender: "", isNew: true },
    ]);
    setRemovedMemberIds([]);
    setErrorMessage({
      riName: null,
      state: null,
      district: null,
      block: null,
      village: null,
      members: [],
    });
    setSelectedRiId("");
  };

  // === VALIDATION BEFORE SUBMIT ===
  const validateAll = () => {
    let valid = true;
    const errs = {
      riName: null,
      state: null,
      district: null,
      block: null,
      village: null,
      members: [],
    };

    if (!riName.trim()) {
      errs.riName = "RI name is required";
      valid = false;
    }

    if (mode === "add") {
      if (!geography.state) {
        errs.state = "Please select a state";
        valid = false;
      }
      if (!geography.district) {
        errs.district = "Please select a district";
        valid = false;
      }
      if (!geography.block) {
        errs.block = "Please select a block";
        valid = false;
      }
      if (!geography.village) {
        errs.village = "Please select a village";
        valid = false;
      }
    }

    members.forEach((m, idx) => {
      const mErr = { first_name: null, phone: null, gender: null };
      if (!m.first_name.trim()) {
        mErr.first_name = "First name is required";
        valid = false;
      }
      if (!/^[0-9]{10,13}$/.test(m.phone)) {
        console.log("🚀 ~ members.forEach ~ m.phone:", m.phone, m.phone.length);
        mErr.phone = "Enter 10-digit phone number";
        valid = false;
      }
      if (!m.gender) {
        mErr.gender = "Please select gender";
        valid = false;
      }
      errs.members[idx] = mErr;
    });

    setErrorMessage(errs);
    return valid;
  };

  // === SUBMIT HANDLER ===
  const handleSubmit = async () => {
    setErrorMessage({
      riName: null,
      state: null,
      district: null,
      block: null,
      village: null,
      members: [],
    });
    if (!validateAll()) {
      if (errorMessage.riName) scrollToError(riNameRef);
      return;
    }

    callLoader(true);

    // Compose full address from geography
    const stateName = states.find((s) => s.id === geography.state)?.name || "";
    const districtName =
      districts.find((d) => d.id === geography.district)?.name || "";
    const blockName = blocks.find((b) => b.id === geography.block)?.name || "";
    const villageName =
      villages.find((v) => v.id === geography.village)?.name || "";
    const fullAddress = `${villageName}, ${blockName}, ${districtName}, ${stateName}`;

    if (mode === "add") {
      // === Add Mode ===
      const payload = {
        name: riName.trim(),
        address: fullAddress,
        village_id: geography.village,
        members: members.map((m) => ({
          first_name: m.first_name.trim(),
          phone: m.phone.trim(),
          gender: m.gender, // include gender
        })),
      };

      try {
        const res = await HTTPService(
          "POST",
          UrlConstants.base_url + UrlConstants.add_ri,
          JSON.stringify(payload),
          false,
          true,
          getTokenLocal()
        );
        if (res.status === 201 || res.status === 200) {
          callToast("RI added successfully!", "success", true);
          clearForm();
        } else {
          callToast("Something went wrong. Please try again.", "error", true);
        }
      } catch (e) {
        console.log("🚀 ~ handleSubmit ~ e:", e);
        if (e?.response?.status === 400 && e.response.data) {
          const data = e.response.data;

          const raw = e.response?.data?.error ?? "Something went wrong";
          let friendly = raw;

          // look for: Key (phone)=(+919099999111)
          const dupeMatch = raw.match(/Key \(([^)]+)\)=\(([^)]+)\)/);
          if (dupeMatch) {
            const [, field, value] = dupeMatch;
            const prettyField = field.replace(/_/g, " ");
            friendly = `The ${prettyField} "${value}" is already registered.`;
          }

          // Build the complete error object in one go
          setErrorMessage((prevErrorMessage) => {
            const updatedErrors = { ...prevErrorMessage };

            // Handle general field errors
            if (data.name) updatedErrors.riName = data.name[0];
            if (data.village_id) updatedErrors.village = data.village_id[0];

            // Handle members errors
            const updatedMembers = [...prevErrorMessage.members];

            // Handle the specific phone duplicate error
            // const error = { index: 0, message: "number already exist" };
            const error = data;
            console.log("🚀 ~ setErrorMessage ~ error:", error);
            if (error.index !== undefined) {
              // Ensure the array has enough elements up to the required index
              while (updatedMembers.length <= error.index) {
                updatedMembers.push({
                  first_name: null,
                  phone: null,
                  gender: null,
                });
              }

              // Update the specific index
              updatedMembers[error.index] = {
                ...updatedMembers[error.index],
                phone: error.error,
              };
            }

            // Handle other member errors from data.members
            if (data.members) {
              data.members.forEach((mErr, idx) => {
                // Ensure array is long enough
                while (updatedMembers.length <= idx) {
                  updatedMembers.push({
                    first_name: null,
                    phone: null,
                    gender: null,
                  });
                }

                // Merge the error with existing error at this index
                updatedMembers[idx] = {
                  ...updatedMembers[idx],
                  ...mErr,
                };
              });
            }

            updatedErrors.members = updatedMembers;

            console.log("🚀 ~ Final error state:", updatedErrors);
            return updatedErrors;
          });

          // callToast(friendly, "error", true);
        } else if (e?.response?.status === 401 || e?.response?.status === 403) {
          navigate("/login");
          callToast("Session expired, please login again!", "error", true);
        } else {
          callToast("Unable to add RI", "error", true);
        }
      } finally {
        callLoader(false);
      }
    } else if (mode === "edit" && selectedRiId) {
      const toAdd = members.filter((m) => m.isNew && m.id === null);
      const toUpdate = members.filter((m) => !m.isNew && m.id); // Existing members to update
      const toRemoveIds = [...removedMemberIds]; // Members to remove

      try {
        // (A) Remove existing members
        for (let member_id of toRemoveIds) {
          await HTTPService(
            "DELETE",
            UrlConstants.base_url +
              `organisation/ri/${selectedRiId}/delete_member/?member_id=${member_id}`,
            "",
            false,
            true,
            getTokenLocal()
          );
        }

        // (B) Add new members
        // (B) Add new members
        for (let i = 0; i < toAdd.length; i++) {
          const m = toAdd[i];
          const payload = {
            first_name: m.first_name.trim(),
            phone: `${m.phone.trim()}`,
            gender: m.gender === "Male" ? "M" : "F", // Map to API format
          };

          try {
            await HTTPService(
              "POST",
              UrlConstants.base_url +
                `/organisation/ri/${selectedRiId}/add_member/`,
              JSON.stringify(payload),
              false,
              true,
              getTokenLocal()
            );
          } catch (memberError) {
            console.log("🚀 ~ Add member error:", memberError);

            // Handle duplicate phone error for this specific member
            if (
              memberError?.response?.status === 400 &&
              memberError?.response?.data?.error &&
              memberError.response.data.error.includes("User already exists")
            ) {
              // Find the index of this member in the original members array
              const memberIndexInForm = members.findIndex(
                (member) =>
                  member.isNew &&
                  member.first_name === m.first_name &&
                  member.phone === m.phone &&
                  member.gender === m.gender
              );

              if (memberIndexInForm >= 0) {
                // Set error for this specific member
                setErrorMessage((prevErrorMessage) => {
                  const updatedErrors = { ...prevErrorMessage };
                  const updatedMembers = [...prevErrorMessage.members];

                  // Ensure the array has enough elements up to the required index
                  while (updatedMembers.length <= memberIndexInForm) {
                    updatedMembers.push({
                      first_name: null,
                      phone: null,
                      gender: null,
                    });
                  }

                  // Set error message for this member's phone
                  updatedMembers[memberIndexInForm] = {
                    ...updatedMembers[memberIndexInForm],
                    phone: "This phone number already exists in the system",
                  };

                  updatedErrors.members = updatedMembers;
                  return updatedErrors;
                });
              }

              // Stop the loader and return early
              callLoader(false);
              return;
            } else {
              // Re-throw other errors
              throw memberError;
            }
          }
        }

        // (C) Update existing members
        for (let m of toUpdate) {
          const payload = {
            member_id: m.id,
            first_name: m.first_name.trim(),
            phone: `${m.phone.trim()}`,
            gender: m.gender === "Male" ? "M" : "F", // Map to API format
          };
          await HTTPService(
            "POST",
            UrlConstants.base_url +
              `organisation/ri/${selectedRiId}/edit_member/`,
            JSON.stringify(payload),
            false,
            true,
            getTokenLocal()
          );
        }

        callToast("Members updated successfully!", "success", true);
        clearForm();
        setMode("choose");
      } catch (e) {
        console.error(e);

        callToast("Unable to update members", "error", true);
      } finally {
        callLoader(false);
      }
    }
  };

  console.log("setErrorMessage", errorMessage);

  // === INITIAL EFFECTS ===
  useEffect(() => {
    // Always fetch states initially
    getStateList();
  }, []);

  // Whenever user clicks “Edit RI”, fetch list of all RIs:
  useEffect(() => {
    if (mode === "choose") {
      setRiLoading(true);
      HTTPService(
        "GET",
        UrlConstants.base_url + "/organisation/ri/",
        "",
        false,
        true,
        getTokenLocal()
      )
        .then((res) => {
          setRiList(res.data);
        })
        .catch(() => {
          callToast("Unable to fetch RI list", "error", true);
          setMode("add"); // revert if something goes wrong
        })
        .finally(() => setRiLoading(false));
    }
  }, [mode]);

  // When user selects an RI to edit, fetch its details:
  useEffect(() => {
    if (mode === "choose" && selectedRiId) {
      callLoader(true);
      HTTPService(
        "GET",
        UrlConstants.base_url + `${UrlConstants.add_ri}${selectedRiId}/`,
        "",
        false,
        true,
        getTokenLocal()
      )
        .then((res) => {
          const data = res.data;
          setRiName(data.name);

          // Fill geography with IDs
          setGeography({
            state: data.village?.state_id,
            district: data.village?.district_id,
            block: data.village?.block_id,
            village: data.village?.id,
          });

          // Populate dropdowns for district/block/village
          getDistrictList(data.village?.state_id);
          getBlockList(data.village?.district_id);
          getVillageList(data.village?.block_id);

          // Populate members (existing)
          let initialMembers = (data.members || []).map((m) => ({
            id: m.id,
            first_name: m.first_name,
            phone: m.phone.replace(/^\+91/, ""),
            gender:
              m.gender == "M" ? "Male" : m.gender == "F" ? "Female" : m.gender, // assume API returns gender
            isNew: false,
          }));

          initialMembers =
            initialMembers?.length <= 0
              ? [
                  {
                    id: null,
                    first_name: "",
                    phone: "",
                    gender: "",
                    isNew: true,
                  },
                ]
              : initialMembers;
          setMembers(initialMembers);
          setErrorMessage((prev) => ({
            ...prev,
            members: initialMembers.map(() => ({
              first_name: null,
              phone: null,
              gender: null,
            })),
          }));

          setMode("edit"); // Switch to “edit” mode now that data is loaded
        })
        .catch((e) => {
          callToast("Unable to fetch RI details", "error", true);
          setMode("add");
        })
        .finally(() => callLoader(false));
    }
  }, [selectedRiId]);

  // Whenever geography updates in edit mode, preload child lists:
  useEffect(() => {
    if (mode === "edit" && geography.state) getDistrictList(geography.state);
  }, [geography.state]);

  useEffect(() => {
    if (mode === "edit" && geography.district) getBlockList(geography.district);
  }, [geography.district]);

  useEffect(() => {
    if (mode === "edit" && geography.block) getVillageList(geography.block);
  }, [geography.block]);

  // Scroll to RI name on error
  useEffect(() => {
    if (errorMessage.riName) scrollToError(riNameRef);
  }, [errorMessage.riName]);

  return (
    <>
      {openSnackbar && (
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          disableWindowBlurListener={true}
          sx={{
            width: "860px",
            marginTop: "50px",
            height: "50px",
            color: "#212B36",
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          action={action}
        >
          <Alert
            severity={"warning"}
            sx={{
              width: "860px",
              textAlign: "left",
              fontFamily: "Montserrat",
              fontWeight: "400",
              fontSize: "14px",
              backgroundColor: "#FFC54E",
              color: "#212B36",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#212B36",
              },
            }}
            action={action}
          >
            {isButtonClicked
              ? "Are you sure you want to clear the form?"
              : "Are you sure you want to go back?"}
          </Alert>
        </Snackbar>
      )}

      <div>
        {/* Back Button */}
        <Row>
          <Col xs={6} sm={6} md={6} lg={6}>
            <Button
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
              }}
              variant="text"
              className={`${LocalStyle.backButton}`}
              onClick={handleBackClick}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Col>
        </Row>

        {/* Title and “Edit RI” Toggle */}
        <Row>
          <Col xs={12} sm={12} md={12} lg={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}
            >
              <Typography className={LocalStyle.titleHead}>
                {mode === "add"
                  ? "Add new RI"
                  : mode === "choose"
                  ? "Select RI to Edit"
                  : "Edit RI Members"}
              </Typography>

              {/* Show “Edit RI” button when in add mode or after clearing */}
              {/* {mode === "add" && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setMode("choose");
                    setErrorMessage({
                      riName: null,
                      state: null,
                      district: null,
                      block: null,
                      village: null,
                      members: [], // one entry per member row, each: { first_name, phone, gender }
                    });
                  }}
                  // <Button
                  // variant="outlined"
                  className={`btn ${LocalStyle.editNavButton}`}
                  style={{ marginTop: "0", maxWidth: "150px" }}
                  //   onClick={() => navigate("/editRI")}
                  // >
                  //   Edit RI
                  // </Button>
                >
                  Edit RI
                </Button>
              )} */}
            </Box>
          </Col>
        </Row>

        {/* If mode === "choose", show a loading spinner or dropdown */}
        {mode === "choose" && (
          <Row>
            <Col xs={6} sm={6} md={6} lg={6}>
              {riLoading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ marginLeft: "10px" }}>
                    Loading RIs…
                  </Typography>
                </Box>
              ) : (
                <FormControl
                  className={LocalStyle.textField}
                  variant="outlined"
                  fullWidth
                  required
                  sx={{ marginBottom: "15px", maxWidth: "544px" }}
                >
                  <InputLabel id="choose-ri-label">RI Name</InputLabel>
                  <Select
                    labelId="choose-ri-label"
                    id="choose-ri"
                    label="RI Name"
                    fullWidth
                    value={selectedRiId}
                    onChange={(e) => setSelectedRiId(e.target.value)}
                  >
                    {riList.map((ri) => (
                      <MenuItem key={ri.id} value={ri.id}>
                        {ri.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Col>
          </Row>
        )}

        {/* MAIN FORM FIELDS */}
        {/* Once mode === "add" or mode === "edit", show the rest of the form */}
        {(mode === "add" || mode === "edit") && (
          <>
            {/* RI Name */}
            <Row>
              <Col xs={6} sm={6} md={6} lg={6}>
                <TextField
                  label="RI name"
                  name="riName"
                  value={riName}
                  inputProps={{ maxLength: 1000 }}
                  onKeyDown={(e) => handleUnwantedSpace(riName, e)}
                  onChange={(e) => {
                    // Only in “add” mode should the user type the name
                    if (mode === "add") {
                      const input = e.target.value;
                      const alphabetsRegex = /^[a-zA-Z\s]*$/;
                      if (alphabetsRegex.test(input)) {
                        setRiName(input);
                        setErrorMessage((prev) => ({
                          ...prev,
                          riName: null,
                        }));
                      }
                    }
                  }}
                  style={{
                    marginBottom: "15px",
                    width: "100%",
                    maxWidth: "544px",
                  }}
                  required
                  className={LocalStyle.textField}
                  id="riName"
                  error={Boolean(errorMessage.riName)}
                  helperText={errorMessage.riName || ""}
                  ref={riNameRef}
                  disabled={mode === "edit"} // read-only in edit
                />
              </Col>
            </Row>

            {/* Geography selects */}
            {mode === "add" && (
              <>
                <Row>
                  <Col xs={6} sm={6} md={6} lg={6}>
                    <FormControl
                      className={LocalStyle.textField}
                      variant="outlined"
                      fullWidth
                      required
                      error={Boolean(errorMessage.state)}
                      sx={{ marginBottom: "15px", maxWidth: "544px" }}
                    >
                      <InputLabel id="state-label">State</InputLabel>
                      <Select
                        labelId="state-label"
                        id="state"
                        label="State"
                        fullWidth
                        value={geography.state}
                        onChange={handleStateChange}
                        disabled={mode === "edit"}
                      >
                        {states.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errorMessage.state && (
                        <FormHelperText>{errorMessage.state}</FormHelperText>
                      )}
                    </FormControl>
                  </Col>

                  <Col xs={6} sm={6} md={6} lg={6}>
                    <FormControl
                      className={LocalStyle.textField}
                      variant="outlined"
                      fullWidth
                      required
                      error={Boolean(errorMessage.district)}
                      sx={{ marginBottom: "15px", maxWidth: "544px" }}
                    >
                      <InputLabel id="district-label">District</InputLabel>
                      <Select
                        labelId="district-label"
                        id="district"
                        label="District"
                        fullWidth
                        value={geography.district}
                        onChange={handleDistrictChange}
                        disabled={mode === "edit"}
                      >
                        {districts.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errorMessage.district && (
                        <FormHelperText>{errorMessage.district}</FormHelperText>
                      )}
                    </FormControl>
                  </Col>
                </Row>

                <Row>
                  <Col xs={6} sm={6} md={6} lg={6}>
                    <FormControl
                      className={LocalStyle.textField}
                      variant="outlined"
                      fullWidth
                      required
                      error={Boolean(errorMessage.block)}
                      sx={{ marginBottom: "15px", maxWidth: "544px" }}
                    >
                      <InputLabel id="block-label">Block</InputLabel>
                      <Select
                        labelId="block-label"
                        id="block"
                        label="Block"
                        fullWidth
                        value={geography.block}
                        onChange={handleBlockChange}
                        disabled={mode === "edit"}
                      >
                        {blocks.map((b) => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errorMessage.block && (
                        <FormHelperText>{errorMessage.block}</FormHelperText>
                      )}
                    </FormControl>
                  </Col>

                  <Col xs={6} sm={6} md={6} lg={6}>
                    <FormControl
                      className={LocalStyle.textField}
                      variant="outlined"
                      fullWidth
                      required
                      error={Boolean(errorMessage.village)}
                      sx={{ marginBottom: "15px", maxWidth: "544px" }}
                    >
                      <InputLabel id="village-label">Village</InputLabel>
                      <Select
                        labelId="village-label"
                        id="village"
                        label="Village"
                        fullWidth
                        value={geography.village}
                        onChange={handleVillageChange}
                        disabled={mode === "edit"}
                      >
                        {villages.map((v) => (
                          <MenuItem key={v.id} value={v.id}>
                            {v.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errorMessage.village && (
                        <FormHelperText>{errorMessage.village}</FormHelperText>
                      )}
                    </FormControl>
                  </Col>
                </Row>
              </>
            )}

            {/* RI Members */}
            <Row style={{ marginTop: "30px" }}>
              <Col xs={12} sm={12} md={12} lg={12}>
                <Typography className={LocalStyle.subTitle}>
                  RI Members
                </Typography>
              </Col>
            </Row>

            {members.map((member, idx) => (
              <Row key={idx} style={{ marginBottom: "15px" }}>
                {/* Member Name */}
                <Col xs={4} sm={4} md={4} lg={4}>
                  <TextField
                    label={`Member ${idx + 1} Name`}
                    value={member.first_name}
                    inputProps={{ maxLength: 255 }}
                    onChange={(e) =>
                      handleMemberChange(idx, "first_name", e.target.value)
                    }
                    style={{ width: "100%", maxWidth: "500px" }}
                    required
                    className={LocalStyle.textField}
                    error={Boolean(errorMessage.members[idx]?.first_name)}
                    helperText={errorMessage.members[idx]?.first_name || ""}
                    // Remove disabled prop
                  />
                </Col>

                {/* Member Phone */}
                <Col xs={3} sm={3} md={3} lg={3}>
                  <TextField
                    label={`Member ${idx + 1} Phone`}
                    value={member.phone.slice(-10)}
                    inputProps={{ maxLength: 10 }}
                    onChange={(e) =>
                      handleMemberChange(idx, "phone", e.target.value)
                    }
                    style={{ width: "100%", maxWidth: "500px" }}
                    required
                    className={LocalStyle.textField}
                    error={Boolean(errorMessage.members[idx]?.phone)}
                    helperText={errorMessage.members[idx]?.phone || ""}
                    // disabled={mode == "edit"}
                    // disabled={mode === "edit" && !member.isNew}
                    // Remove disabled prop
                  />
                </Col>

                {/* Member Gender */}
                <Col xs={3} sm={3} md={3} lg={3}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    required
                    error={Boolean(errorMessage.members[idx]?.gender)}
                    sx={{ maxWidth: 170, marginTop: "15px" }}
                  >
                    <InputLabel id={`gender-label-${idx}`}>Gender</InputLabel>
                    <Select
                      labelId={`gender-label-${idx}`}
                      id={`gender-${idx}`}
                      value={member.gender}
                      label="Gender"
                      onChange={(e) =>
                        handleMemberChange(idx, "gender", e.target.value)
                      }
                      // Remove disabled prop
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                    {errorMessage.members[idx]?.gender && (
                      <FormHelperText>
                        {errorMessage.members[idx]?.gender}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Col>

                {/* Add/Remove Button */}
                <Col
                  xs={2}
                  sm={2}
                  md={2}
                  lg={2}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  {/* Remove button (if >1 row) */}
                  {members.length > 1 && (
                    <Button
                      className={LocalStyle.removeBtn}
                      onClick={() => removeMemberRow(idx)}
                      style={{
                        marginBottom: idx === members.length - 1 ? "5px" : 0,
                      }}
                    >
                      Remove
                    </Button>
                  )}

                  {/* + Add Member on the last row */}
                  {idx === members.length - 1 && (
                    <Button
                      className={LocalStyle.addMoreBtn}
                      onClick={addMemberRow}
                      style={{ marginTop: idx >= 1 ? 0 : "5px" }}
                    >
                      + Add Member
                    </Button>
                  )}
                </Col>
              </Row>
            ))}

            {/* Footer Buttons */}
            <Row style={{ marginTop: "40px", marginBottom: "100px" }}>
              {mode == "add" && (
                <Col xs={6} sm={6} md={6} lg={6}>
                  <Button
                    variant="outlined"
                    className={LocalStyle.primaryButton}
                    onClick={handleClearClick}
                  >
                    Clear form
                  </Button>
                </Col>
              )}

              <Col
                xs={6}
                sm={6}
                md={6}
                lg={6}
                className={LocalStyle.buttonStyle}
              >
                <Button
                  variant="contained"
                  className={LocalStyle.saveBtn}
                  onClick={handleSubmit}
                >
                  {mode === "edit" ? "Save Changes" : "Save"}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
}
